import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/* ============================================================================
 * InstallPrompt — shown on the login page before sign-in.
 *
 * Setup (once), in src/index.js, ABOVE the ReactDOM.createRoot line:
 *
 *   window.addEventListener('beforeinstallprompt', (e) => {
 *     e.preventDefault();                    // stop Chrome's mini-infobar
 *     window.__snlInstallPrompt = e;         // keep it for the button below
 *   });
 *
 * Usage, in the login page component:
 *
 *   import InstallPrompt from '../components/InstallPrompt';
 *   ...
 *   return (
 *     <>
 *       <InstallPrompt />
 *       ...existing login form...
 *     </>
 *   );
 *
 * Behaviour:
 *   - Hidden when already running as an installed app.
 *   - "Install app" button where the browser supports one-tap install
 *     (Chrome/Edge desktop, Chrome/Samsung on Android).
 *   - Step-by-step instructions everywhere else (iPhone/iPad Safari,
 *     macOS Safari, Firefox → recommends Chrome/Edge).
 *   - "Not now" hides it for 7 days; "Don't show again" for a year.
 * ==========================================================================*/

const DISMISS_KEY = 'snl_install_prompt_hide_until';
const DAY = 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const detect = () => {
  const ua = navigator.userAgent || '';
  const brands = navigator.userAgentData?.brands?.map((b) => b.brand) ?? [];

  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS
  const isAndroid = /Android/.test(ua);
  const isMac = !isIOS && /Macintosh/.test(ua);

  const isEdge = brands.includes('Microsoft Edge') || /Edg\//.test(ua);
  const isChrome =
    !isEdge && (brands.includes('Google Chrome') || (/Chrome\//.test(ua) && !/OPR|SamsungBrowser/.test(ua)));
  const isSamsung = /SamsungBrowser/.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge && !isFirefox && !isSamsung && !/CriOS|FxiOS/.test(ua);

  if (isIOS) return isSafari ? 'ios-safari' : 'ios-other';
  if (isAndroid) return isChrome || isSamsung || isEdge ? 'android-chrome' : 'android-other';
  if (isMac && isSafari) return 'mac-safari';
  if (isFirefox) return 'firefox';
  if (isChrome || isEdge) return 'desktop-chrome';
  return 'other';
};

const CONTENT = {
  'ios-safari': {
    heading: 'Add SNL to your home screen',
    steps: [
      'Tap the Share button at the bottom of Safari (the square with an arrow).',
      'Scroll down and tap "Add to Home Screen".',
      'Tap "Add" in the top right.',
    ],
    note: 'The app then opens full screen from your home screen, like any other app.',
  },
  'ios-other': {
    heading: 'Open this page in Safari to install',
    steps: [
      'Copy this page\'s address.',
      'Open Safari and paste it in.',
      'Tap Share, then "Add to Home Screen", then "Add".',
    ],
    note: 'On iPhone and iPad, only Safari can add the app to your home screen.',
  },
  'android-chrome': {
    heading: 'Install SNL on this phone',
    steps: [
      'Tap the ⋮ menu in the top right of the browser.',
      'Tap "Add to Home screen" or "Install app".',
      'Tap "Install".',
    ],
    note: 'Or use the Install app button below if it appears.',
  },
  'android-other': {
    heading: 'Use Chrome to install',
    steps: [
      'Open this page in Chrome (or Samsung Internet).',
      'Tap the ⋮ menu, then "Add to Home screen".',
      'Tap "Install".',
    ],
    note: 'Other Android browsers may not support installing.',
  },
  'desktop-chrome': {
    heading: 'Install SNL on this computer',
    steps: [
      'Click the install icon at the right end of the address bar (a screen with a down arrow).',
      'Click "Install".',
    ],
    note: 'Or use the Install app button below. The app then opens in its own window from your Start menu or Dock.',
  },
  'mac-safari': {
    heading: 'Add SNL to your Dock',
    steps: [
      'In the Safari menu bar, choose File, then "Add to Dock".',
      'Click "Add".',
    ],
    note: 'Requires macOS Sonoma or later. Chrome or Edge also work.',
  },
  firefox: {
    heading: 'Use Chrome or Edge to install',
    steps: [
      'Open this page in Chrome or Microsoft Edge.',
      'Click the install icon in the address bar.',
      'Click "Install".',
    ],
    note: 'Firefox can\'t install web apps. You can keep using SNL in Firefox as a normal website.',
  },
  other: {
    heading: 'Install SNL as an app',
    steps: [
      'Open this page in Chrome or Microsoft Edge.',
      'Use the install icon in the address bar, or the browser menu, then "Install".',
    ],
    note: 'On iPhone and iPad, use Safari: Share, then "Add to Home Screen".',
  },
};

const InstallPrompt = () => {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState('other');
  const [canPrompt, setCanPrompt] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const hideUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() < hideUntil) return;

    setPlatform(detect());
    setCanPrompt(Boolean(window.__snlInstallPrompt));
    setOpen(true);

    // If the browser's prompt event arrives after mount, pick it up.
    const onPrompt = () => setCanPrompt(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const hideFor = (days) => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + days * DAY));
    setOpen(false);
  };

  const install = async () => {
    const evt = window.__snlInstallPrompt;
    if (!evt) return;
    evt.prompt();
    const { outcome } = await evt.userChoice;
    window.__snlInstallPrompt = null;
    if (outcome === 'accepted') hideFor(365);
  };

  if (!open) return null;
  const c = CONTENT[platform];

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4"
      onClick={() => hideFor(7)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-heading"
    >
      <div
        className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md text-white"
        style={{ border: '1px solid #22d3ee' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 id="install-heading" className="text-lg font-semibold">
            {c.heading}
          </h3>
          <button
            onClick={() => hideFor(7)}
            className="text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 rounded"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pb-2 text-sm text-gray-200">
          <p className="mb-3">
            SNL works best installed as an app: it opens from your home screen or desktop and
            fills the whole screen.
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            {c.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
          <p className="mt-3 text-gray-400">{c.note}</p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4">
          <button
            onClick={() => hideFor(365)}
            className="text-sm text-gray-400 hover:text-white mr-auto focus:outline-none focus:ring-2 focus:ring-cyan-400 rounded px-1"
          >
            Don't show again
          </button>
          <button
            onClick={() => hideFor(7)}
            className="px-4 py-2 rounded-lg text-sm bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          >
            Not now
          </button>
          {canPrompt && (
            <button
              onClick={install}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-200"
              style={{ backgroundColor: '#22d3ee' }}
            >
              Install app
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;